<script setup lang="ts">
import { computed } from 'vue'
import { TAB_BUTTON_POSITION, TAB_ICONS_NAMES } from '@/utils/tabs'
import { cn } from '@/utils/cn'
import { useIdeStore } from '@/stores/ide.store'
import { useTabStore } from '@/stores/tabs.store'
import TabsSettings from '@/components/ide/ide/TabsSettings.vue'
import SideBarTooltip from '@/components/ide/ide/SettingsSideBarTooltip.vue'
import closeIcon from '@/assets/images/ide/closebutton.svg'
import tabsSettingIcon from '@/assets/images/ide/dots-vertical.svg'

let timeoutId: NodeJS.Timeout | null = null

const props = defineProps({
  titleName: {
    type: String,
    required: true
  },
  icon: {
    type: String,
    required: true
  },
  tabPosition: {
    type: String,
    required: false
  },
  tabName: {
    type: String,
    required: false
  },
  customClass: {
    type: Boolean,
    required: false
  }
})

const ideStore = useIdeStore()
const tabStore = useTabStore()

const showTabSettings = computed(() => {
  return tabStore.openTabSettings.visible && tabStore.openTabSettings.position == props.tabPosition
})

/**
 * Opens the tabs settings for a specific tab.
 * @param tabPosition - defines which tab is on hover
 */
const handleMouseEnter = (tabPosition: string | undefined) => {
  if (timeoutId) {
    clearTimeout(timeoutId)
    timeoutId = null
  }
  if (tabPosition) {
    tabStore.setTabSettings({ visible: true, position: tabPosition })
  }
}

/**
 * Hide menu on mouse leave after short delay
 *   @param tabPosition - defines which tab is on hover
 */
const handleMouseLeave = (tabPosition: string | undefined) => {
  timeoutId = setTimeout(() => {
    if (tabPosition) {
      tabStore.setTabSettings({
        visible: !tabStore.openTabSettings.visible,
        position: tabPosition
      })
    }
  }, 250) // 250ms second delay
}
/**
 * close the current active tab
 */
const handleCloseTab = () => {
  tabStore.setActiveTabPosition(
    props.tabPosition as TAB_BUTTON_POSITION,
    props.tabName as TAB_ICONS_NAMES
  )
}
</script>

<template>
  <div
    :class="
      cn('background-quaternary stroke-primary-default flex min-h-10 w-full justify-between', {
        'border-b sm:border-y':
          props.tabPosition == TAB_BUTTON_POSITION.BOTTOM ||
          props.tabPosition == TAB_BUTTON_POSITION.MAXIMIZE,
        'border-b': props.tabPosition == TAB_BUTTON_POSITION.RIGHT && !props.customClass
      })
    "
  >
    <div
      class="flex w-full min-w-fit items-center gap-2 sm:w-auto"
      :class="props.customClass ? 'p-0' : 'p-2'"
    >
      <img :src="props.icon" class="h-5 w-5" alt="JDroid" />
      <div class="text-high text-sm font-normal">{{ props.titleName }}</div>
      <slot></slot>
    </div>

    <div v-if="ideStore.currentMobileTab == null" class="flex min-w-fit items-center gap-2 p-2">
      <div
        v-if="
          props.tabPosition == TAB_BUTTON_POSITION.RIGHT ||
          props.tabPosition == TAB_BUTTON_POSITION.BOTTOM
        "
        class="flex-end flex items-center justify-center"
      >
        <button
          @mouseenter="handleMouseEnter(props.tabPosition)"
          @mouseleave="handleMouseLeave(props.tabPosition)"
        >
          <img :src="tabsSettingIcon" class="h-4.5 w-4.5" />
        </button>
      </div>
      <div
        v-if="
          props.tabPosition == TAB_BUTTON_POSITION.RIGHT ||
          props.tabPosition == TAB_BUTTON_POSITION.BOTTOM
        "
        class="hs-tooltip flex py-2 [--placement:left]"
      >
        <button type="button" class="hs-tooltip-toggle h-3 w-3" @click="handleCloseTab">
          <img :src="closeIcon" />
        </button>
        <SideBarTooltip content="Close" />
      </div>
    </div>
  </div>
  <!-- Menu -->
  <div v-if="showTabSettings" class="absolute right-1 top-8 z-40">
    <TabsSettings
      :tabName="tabName"
      :currentPosition="props.tabPosition"
      @mouseenter="handleMouseEnter(props.tabPosition)"
      @mouseleave="handleMouseLeave(props.tabPosition)"
    />
  </div>
</template>
